import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getTotalData, getBranchComparison } from "../../Endpoint/Admin/index";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C, Panel, StatCard, StatRow, SearchPill, Table, Chip,
} from "../../components/Console/ConsoleUI";
import PeriodFilter, { today, periodToParams, periodLabel } from "../../components/Common/PeriodFilter";
import {
  isBranchScopedAdmin, getUserBranchName, canAccessAllBranches,
} from "../../services/PermissionService";

/**
 * Operations board — screen A2 of the console redesign.
 *
 * Opens with four KPI tiles, then revenue by service beside the branch table.
 * Data comes from the same /admin/totalData call the previous dashboard used,
 * plus the branch comparison built for the reporting work; nothing new is
 * invented client-side.
 *
 * The period is the shared PeriodFilter with its Today preset, which stays the
 * default: this is the board people open to see how the day is going.
 *
 * Figures respect the branch in effect — the API scopes them from the request,
 * so switching branch in the rail changes what this shows.
 */

const dayLabel = () =>
  new Date().toLocaleDateString(undefined, {
    weekday: "long", day: "numeric", month: "long",
  });

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

const Dashboard = () => {
  const [period, setPeriod] = useState(today);
  const [kpis, setKpis] = useState({ orders: 0, deliveries: 0, roomsBooked: 0, transportRequests: 0 });
  const [branches, setBranches] = useState([]);
  const [totals, setTotals] = useState({ revenue: 0, orders: 0 });
  const [search, setSearch] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getTotalData(periodToParams(period)).then((result) => {
      if (result && result.data) setKpis(result.data);
      else toast.error("Could not load the dashboard figures");
      setLoaded(true);
    });
  }, [period]);

  useEffect(() => {
    // Only a global admin sees a meaningful comparison; a scoped admin gets a
    // single row, so the panel is not worth the request for them.
    if (!canAccessAllBranches()) return;
    getBranchComparison(periodToParams(period)).then((res) => {
      const d = res && res.data;
      if (!d) return;
      setBranches(d.branches || []);
      setTotals(d.totals || { revenue: 0, orders: 0 });
    });
  }, [period]);

  const scopeName = isBranchScopedAdmin() ? getUserBranchName() : null;
  const when = periodLabel(period);
  const title = scopeName ? `${when} at ${scopeName}` : `${when} across all branches`;

  const visibleBranches = branches.filter((b) =>
    !search.trim() ? true : (b.name || "").toLowerCase().includes(search.trim().toLowerCase())
  );

  return (
    <ConsolePage
      title={title}
      subtitle={`${dayLabel()}${loaded ? "" : " · loading…"}`}
      actions={
        canAccessAllBranches() && (
          <SearchPill value={search} onChange={setSearch} placeholder="Search branches" />
        )
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
        <PeriodFilter withToday value={period} onChange={setPeriod} />
        <StatRow>
          <StatCard icon="receipt_long" value={kpis.orders ?? 0} label="Orders received" />
          <StatCard icon="room_service" value={kpis.deliveries ?? 0} label="Orders delivered" />
          <StatCard icon="hotel" value={kpis.roomsBooked ?? 0} label="Rooms booked" />
          <StatCard icon="local_taxi" value={kpis.transportRequests ?? 0} label="Transport requests" />
        </StatRow>

        {canAccessAllBranches() ? (
          <div style={{ display: "grid", gridTemplateColumns: "1.55fr 1fr", gap: 18, alignItems: "start" }}>
            <Panel
              title="Revenue by branch"
              subtitle={`${when} · USD`}
            >
              <div style={{ marginTop: 14, display: "flex", alignItems: "baseline", gap: 12 }}>
                <div style={{ fontSize: 32, fontWeight: 500, color: C.ink }}>{money(totals.revenue)}</div>
                <div style={{ fontSize: 14, color: C.taupe }}>
                  {totals.orders || 0} order{totals.orders === 1 ? "" : "s"} across {branches.length} branch
                  {branches.length === 1 ? "" : "es"}
                </div>
              </div>

              {/* Share-of-revenue bars. Rendered from the same numbers as the
                  table beside them, so the two can never disagree. */}
              <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 14 }}>
                {visibleBranches.length === 0 && (
                  <div style={{ color: C.taupeLight, fontSize: 14 }}>No branch revenue in this period.</div>
                )}
                {visibleBranches.map((b) => (
                  <div key={b.branchId || b.code}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: C.brownDeep }}>
                      <span>{b.name}</span>
                      <span style={{ color: C.taupe }}>
                        {money(b.revenue.total)} · {b.revenueShare}%
                      </span>
                    </div>
                    <div style={{ marginTop: 6, height: 10, borderRadius: 5, background: C.tint, overflow: "hidden" }}>
                      <div
                        style={{
                          width: `${Math.max(b.revenueShare, b.revenue.total > 0 ? 2 : 0)}%`,
                          height: "100%",
                          background: C.brown,
                          borderRadius: 5,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel title="Branch performance" subtitle="Orders by service">
              <div style={{ marginTop: 12 }}>
                <Table
                  rowKey="branchId"
                  columns={[
                    { key: "name", label: "Branch" },
                    { key: "totalOrders", label: "Orders", align: "right" },
                    {
                      key: "revenue",
                      label: "Revenue",
                      align: "right",
                      render: (r) => money(r.revenue.total),
                    },
                  ]}
                  rows={visibleBranches}
                  empty="No branch activity yet"
                />
              </div>
            </Panel>
          </div>
        ) : (
          <Panel
            title="Your branch"
            subtitle="Figures above are for the branch you manage"
          >
            <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 10 }}>
              <Chip tone="neutral">
                <span className="ms" style={{ fontSize: 16 }}>apartment</span>
                {scopeName || "—"}
              </Chip>
              <span style={{ fontSize: 14, color: C.taupe }}>
                Every total on this page is filtered to it.
              </span>
            </div>
          </Panel>
        )}
      </div>
    </ConsolePage>
  );
};

export default Dashboard;
